/*
 * The announcement strip's behavior (sections/bakes-announcement.liquid), a
 * port of the site's components/AnnouncementBar.tsx. Plain JS, no build step.
 *
 * Rotation: each message holds HOLD_MS, then fades all the way out over
 * FADE_MS before the next one fades in, so two lines of gold type are never
 * on top of each other. It pauses while the pointer is over the strip or focus
 * is inside it, and stops for a visitor who prefers reduced motion (checked on
 * every tick, so turning it on mid-visit stops it too). That visitor sees the
 * first message only.
 *
 * Dismiss: collapses the strip and sets the --announce-h token to 0px, so the
 * body padding and the navbar move up with it. On the site the strip stayed
 * dismissed across in-app navigations and came back on a full reload; every
 * navigation here is a page load, so the dismissal is kept for the browser
 * session in sessionStorage (bakes:announce_dismissed) and read before first
 * paint by the inline script in layout/theme.liquid, which keeps the 32px
 * from jumping on the next page.
 *
 * MOTION PREVIEW, "Announcement bar rolls" (mo-strip, pick 8,
 * docs/motion-preview.md). Asked on every tick and on every change from the
 * MOTION panel (bakes:motion-change), so it switches without a reload:
 * - The roll: the bar is marked data-roll, the inline fades are taken off
 *   the messages and classes drive them (src/styles/bakes.css): the old
 *   line goes up 6px and out in ROLL_MS, then the new one rises 6px into
 *   place in ROLL_MS. Still a sequence, never two lines on top of each
 *   other (this file's own rule), only quicker: 360ms with the strip never
 *   empty for longer than a frame, against 1200ms today.
 * - The music line (data-announce-music) joins the rotation after the first
 *   message. With the idea off it is never shown.
 * - Home page: the rotation holds while the hero ([data-home-hero]) is on
 *   screen, so the strip is not a second moving thing beside the drone
 *   video.
 * - Black Friday to Cyber Monday (data-announce-bfcm): from data-bfcm-from
 *   to data-bfcm-until the bar is marked data-bfcm and the strip holds that
 *   one line, without rotating; at the end time it is taken away and the
 *   messages come back. On a preview theme (never the live one) the MOTION
 *   panel's review switch, localStorage "bakes-motion-bfcm", shows it now
 *   (event bakes:bfcm-force).
 * With the idea off, everything above is today's code path.
 *
 * HOLIDAY AND SALES (docs/HOLIDAY-2026-SWITCHES.md): a message marked
 * data-hol-show or data-hol-hide is in the rotation only while the CSS
 * shows it (its moment is on now); the strip re-forms on
 * bakes:holiday-change, the head gate's event at each start and end.
 */
(function () {
  "use strict";

  var HOLD_MS = 5000;
  var FADE_MS = 600;
  var ROLL_MS = 180;
  var DISMISS_KEY = "bakes:announce_dismissed";
  var FORCE_KEY = "bakes-motion-bfcm";
  var MAX_TIMER_MS = 2147483647;
  var live = [];

  function rolling() {
    return document.documentElement.classList.contains("mo-strip");
  }

  function paint(el, shown) {
    // Hidden messages use visibility as well as opacity, so they drop out of
    // the tab order and the accessibility tree. The delay lets the outgoing
    // one finish fading before it is hidden.
    el.style.opacity = shown ? "1" : "0";
    el.style.visibility = shown ? "visible" : "hidden";
    el.style.transition = shown
      ? "opacity " + FADE_MS + "ms ease, visibility 0s linear 0s"
      : "opacity " + FADE_MS + "ms ease, visibility 0s linear " + FADE_MS + "ms";
  }

  // The roll's messages are styled by class; inline values would win.
  function unpaint(el) {
    el.style.removeProperty("opacity");
    el.style.removeProperty("visibility");
    el.style.removeProperty("transition");
  }

  // The messages the rotation may show right now: the music line only
  // while the roll is on, and none the holiday switch hides (display none
  // from the CSS while its moment is off, docs/HOLIDAY-2026-SWITCHES.md).
  function shownByHoliday(m) {
    if (!m.hasAttribute("data-hol-show") && !m.hasAttribute("data-hol-hide")) return true;
    try {
      return window.getComputedStyle(m).display !== "none";
    } catch (e) {
      return true;
    }
  }
  function eligible(state) {
    var roll = rolling();
    return state.msgs.filter(function (m) {
      return (roll || !m.hasAttribute("data-announce-music")) && shownByHoliday(m);
    });
  }

  function forced() {
    try {
      var S = window.Shopify;
      return !!(S && S.theme && S.theme.role !== "main") && window.localStorage.getItem(FORCE_KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  // Black Friday: marks the bar for the window (or the review switch) and
  // sets a timer for the next edge, start or end, within setTimeout's range.
  function bfcmSync(state) {
    var bar = state.bar;
    window.clearTimeout(state.bfcmTimer);
    if (!bar.querySelector("[data-announce-bfcm]")) {
      state.bfcm = false;
      return;
    }
    var now = Date.now();
    var from = +bar.getAttribute("data-bfcm-from") || 0;
    var until = +bar.getAttribute("data-bfcm-until") || 0;
    var on = forced() || (now >= from && now < until);
    if (on) bar.setAttribute("data-bfcm", "");
    else bar.removeAttribute("data-bfcm");
    state.bfcm = on;
    var next = now < from ? from : now < until ? until : 0;
    if (next && next - now < MAX_TIMER_MS) {
      state.bfcmTimer = window.setTimeout(function () { bfcmSync(state); }, next - now + 50);
    }
  }

  // Puts the strip in the form the gate asks for now: the roll (classes) or
  // today's inline fades. A swap in flight is dropped; its message stays.
  function sync(state) {
    window.clearTimeout(state.swap);
    state.swap = 0;
    var list = eligible(state);
    if (list.indexOf(state.cur) < 0) state.cur = list[0];
    var roll = rolling();
    if (roll) state.bar.setAttribute("data-roll", "");
    else state.bar.removeAttribute("data-roll");
    for (var i = 0; i < state.msgs.length; i++) {
      var m = state.msgs[i];
      m.classList.remove("is-out");
      if (roll) {
        unpaint(m);
        m.classList.toggle("is-in", m === state.cur);
      } else {
        m.classList.remove("is-in");
        paint(m, m === state.cur);
      }
    }
    bfcmSync(state);
  }

  // Today's swap: all the way out, then in.
  function fadeTo(state, next) {
    paint(state.cur, false);
    state.swap = window.setTimeout(function () {
      state.swap = 0;
      state.cur = next;
      paint(next, true);
    }, FADE_MS);
  }

  // The roll: out and up, then in from below.
  function rollTo(state, next) {
    var prev = state.cur;
    prev.classList.remove("is-in");
    prev.classList.add("is-out");
    state.swap = window.setTimeout(function () {
      state.swap = 0;
      prev.classList.remove("is-out");
      state.cur = next;
      next.classList.add("is-in");
    }, ROLL_MS);
  }

  function init(bar) {
    if (bar.hasAttribute("data-announce-ready")) return;
    bar.setAttribute("data-announce-ready", "");

    var msgs = Array.prototype.slice.call(bar.querySelectorAll("[data-announce-msg]"));
    var dismissBtn = bar.querySelector("[data-announce-dismiss]");
    // The message the server printed visible is the one on screen now (of
    // those the holiday switch shows: a moment's first message is printed
    // visible beside the normal one it replaces).
    var first = msgs.filter(function (m) { return m.style.opacity === "1" && shownByHoliday(m); })[0] || msgs[0];
    var state = { bar: bar, msgs: msgs, cur: first, timer: 0, swap: 0, bfcm: false, bfcmTimer: 0, heroInView: false };
    // Plain flags, not attributes: pausing must not touch the strip's markup.
    var hovered = false;
    var focused = false;

    bar.addEventListener("mouseenter", function () { hovered = true; });
    bar.addEventListener("mouseleave", function () { hovered = false; });
    bar.addEventListener("focusin", function () { focused = true; });
    bar.addEventListener("focusout", function () { focused = false; });

    // Home page (mo-strip): the hero on screen holds the rotation.
    var hero = document.querySelector("[data-home-hero]");
    if (hero && "IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        state.heroInView = entries[entries.length - 1].isIntersecting;
      }).observe(hero);
    }

    sync(state);

    if (msgs.length > 1) {
      var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
      state.timer = window.setInterval(function () {
        if (reduce.matches || hovered || focused || state.swap) return;
        var roll = rolling();
        if (roll && (state.bfcm || state.heroInView)) return;
        var list = eligible(state);
        if (list.length < 2) return;
        var next = list[(list.indexOf(state.cur) + 1) % list.length];
        if (roll) rollTo(state, next);
        else fadeTo(state, next);
      }, HOLD_MS);
    }

    if (dismissBtn) {
      dismissBtn.addEventListener("click", function () {
        stop(state);
        var root = document.documentElement;
        // The one token every top offset reads: body padding and the navbar
        // move up together with the strip.
        root.style.setProperty("--announce-h", "0px");
        root.setAttribute("data-announce-off", "");
        bar.style.display = "none";
        var inEditor = !!(window.Shopify && window.Shopify.designMode);
        if (!inEditor) {
          try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* this page still holds it */ }
        }
      });
    }

    live.push(state);
  }

  function stop(state) {
    window.clearInterval(state.timer);
    if (state.swap) window.clearTimeout(state.swap);
    window.clearTimeout(state.bfcmTimer);
  }

  function initAll() {
    var bars = document.querySelectorAll("[data-announce]");
    for (var i = 0; i < bars.length; i++) init(bars[i]);
  }

  // The MOTION panel (preview themes): a change to mo-strip re-forms the
  // strip at once, and the review switch shows or hides the Black Friday
  // line.
  window.addEventListener("bakes:motion-change", function (e) {
    if (!e.detail || e.detail.key !== "strip") return;
    for (var i = 0; i < live.length; i++) sync(live[i]);
  });
  window.addEventListener("bakes:bfcm-force", function () {
    for (var i = 0; i < live.length; i++) bfcmSync(live[i]);
  });
  // The holiday switch at a start or an end (the head gate's timer): the
  // strip re-forms around the messages now shown.
  window.addEventListener("bakes:holiday-change", function () {
    for (var i = 0; i < live.length; i++) sync(live[i]);
  });

  // Theme editor: a re-rendered section brings a new strip, shown again even
  // if the old one was dismissed; the old one's timers go with it.
  document.addEventListener("shopify:section:unload", function (e) {
    var section = e.target;
    live = live.filter(function (s) {
      if (!section || !section.contains || !section.contains(s.bar)) return true;
      stop(s);
      return false;
    });
  });
  document.addEventListener("shopify:section:load", function (e) {
    var section = e.target;
    if (section && section.querySelector && section.querySelector("[data-announce]")) {
      var root = document.documentElement;
      root.removeAttribute("data-announce-off");
      root.style.removeProperty("--announce-h");
    }
    initAll();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
})();
